// Reads the file Etsy gives you from Shop Manager > Settings > Options >
// Download Data > "Currently for sale listings" and turns it into a list of
// listings. Nothing here talks to the database: see etsyImport.js for that.
//
// parseEtsyCsv(text) -> { listings: [...], problems: [string] }
// Each listing: { row, etsyListingId, sku, name, description, price, currency,
//   quantity, tags, materials, imageUrls, variations, problems }
// `problems` (top level) is every plain-English problem in the file,
// each listing's own problems are also on listing.problems.

export var MAX_ETSY_IMAGES = 10;
export var MAX_ETSY_VARIATIONS = 5;

// ---------------------------------------------------------------------
// CSV
// ---------------------------------------------------------------------

// Splits CSV text into rows of cells. Handles "quoted, cells", "" inside
// quotes, line breaks inside quotes, \r\n or \n endings and a leading BOM.
export function parseCsvRows(text) {
  var s = String(text || '');
  if (s.charCodeAt(0) === 0xfeff) s = s.slice(1);
  var rows = [];
  var row = [];
  var cell = '';
  var inQuotes = false;
  var i = 0;
  var n = s.length;
  while (i < n) {
    var c = s[i];
    if (inQuotes) {
      if (c === '"') {
        if (s[i + 1] === '"') { cell += '"'; i += 2; continue; }
        inQuotes = false; i++; continue;
      }
      cell += c; i++; continue;
    }
    if (c === '"' && cell === '') { inQuotes = true; i++; continue; }
    if (c === ',') { row.push(cell); cell = ''; i++; continue; }
    if (c === '\r' || c === '\n') {
      row.push(cell); cell = '';
      rows.push(row); row = [];
      if (c === '\r' && s[i + 1] === '\n') i++;
      i++; continue;
    }
    cell += c; i++;
  }
  if (cell !== '' || row.length) { row.push(cell); rows.push(row); }
  return { rows: rows, unclosedQuote: inQuotes };
}

// "Variation 1 Type" / "VARIATION_1_TYPE" -> "VARIATION_1_TYPE"
export function headerKey(h) {
  return String(h || '').trim().toUpperCase().replace(/[^A-Z0-9]+/g, '_').replace(/^_+|_+$/g, '');
}

// ---------------------------------------------------------------------
// Cleaning single values
// ---------------------------------------------------------------------

var NAMED_ENTITIES = { amp: '&', quot: '"', apos: "'", lt: '<', gt: '>', nbsp: ' ', hellip: '…', mdash: '—', ndash: '–', rsquo: '’', lsquo: '‘', rdquo: '”', ldquo: '“' };

// Etsy writes some characters as HTML codes (&quot; &#39;). Turn them back.
export function decodeEntities(text) {
  return String(text || '').replace(/&(#x[0-9a-f]+|#\d+|[a-z]+);/gi, function(all, code) {
    if (code[0] === '#') {
      var num = code[1] === 'x' || code[1] === 'X' ? parseInt(code.slice(2), 16) : parseInt(code.slice(1), 10);
      if (!isFinite(num) || num <= 0 || num > 0x10ffff) return all;
      try { return String.fromCodePoint(num); } catch (e) { return all; }
    }
    var named = NAMED_ENTITIES[code.toLowerCase()];
    return named !== undefined ? named : all;
  });
}

function clean(text) {
  return decodeEntities(text).trim();
}

// "a, b ,, A" -> ['a', 'b'] (blanks and repeats dropped, case-insensitive)
export function splitList(text) {
  var seen = {};
  var out = [];
  clean(text).split(',').forEach(function(part) {
    var v = part.trim().replace(/\s+/g, ' ');
    var k = v.toLowerCase();
    if (v && !seen[k]) { seen[k] = true; out.push(v); }
  });
  return out;
}

// "$1,250.00" -> 1250, "" or "abc" -> null
export function parsePrice(text) {
  var t = String(text || '').replace(/[\s$£€,]/g, '');
  if (!/^\d+(\.\d+)?$/.test(t)) return null;
  return Math.round(parseFloat(t) * 100) / 100;
}

function parseQuantity(text) {
  var t = String(text || '').replace(/[\s,]/g, '');
  if (!/^\d+$/.test(t)) return null;
  return parseInt(t, 10);
}

function isHttpsUrl(text) {
  return /^https:\/\/[^\s]+$/i.test(text);
}

// ---------------------------------------------------------------------
// The file
// ---------------------------------------------------------------------

var KNOWN = {
  TITLE: true, DESCRIPTION: true, PRICE: true, CURRENCY_CODE: true, CURRENCY: true,
  QUANTITY: true, TAGS: true, MATERIALS: true, SKU: true, LISTING_ID: true,
};
for (var k = 1; k <= MAX_ETSY_IMAGES; k++) KNOWN['IMAGE' + k] = true;
for (var v = 1; v <= MAX_ETSY_VARIATIONS; v++) {
  KNOWN['VARIATION_' + v + '_TYPE'] = true;
  KNOWN['VARIATION_' + v + '_NAME'] = true;
  KNOWN['VARIATION_' + v + '_VALUES'] = true;
}

export function parseEtsyCsv(text) {
  var problems = [];
  var listings = [];

  var parsed = parseCsvRows(text);
  var rows = parsed.rows.filter(function(r) {
    return r.some(function(cell) { return String(cell).trim() !== ''; });
  });
  if (!rows.length) {
    return { listings: [], problems: ['The file is empty.'] };
  }
  if (parsed.unclosedQuote) {
    problems.push('The file seems to be cut off (a quote mark is never closed). The last listing may be incomplete.');
  }

  var headers = rows[0].map(headerKey);
  var col = {};
  headers.forEach(function(h, i) { if (h && col[h] === undefined) col[h] = i; });

  if (col.TITLE === undefined) {
    return {
      listings: [],
      problems: ['This doesn’t look like an Etsy listings file: there is no TITLE column. In Etsy, use Shop Manager > Settings > Options > Download Data > "Currently for sale listings".'],
    };
  }

  var unused = rows[0].filter(function(h, i) { return headers[i] && !KNOWN[headers[i]]; })
    .map(function(h) { return String(h).trim(); });
  if (unused.length) {
    problems.push('These columns are not used by the shop and were left out: ' + unused.join(', ') + '.');
  }
  if (col.PRICE === undefined) problems.push('There is no PRICE column, so imported products will have a price of 0.');

  function get(row, key) {
    var at = col[key];
    return at === undefined ? '' : String(row[at] === undefined ? '' : row[at]);
  }

  var seenIds = {};

  for (var r = 1; r < rows.length; r++) {
    var row = rows[r];
    var rowNumber = r + 1; // as a spreadsheet shows it (row 1 = headings)
    var name = clean(get(row, 'TITLE')).replace(/\s+/g, ' ');
    var label = 'Row ' + rowNumber + (name ? ' (' + shorten(name) + ')' : '');
    if (!name) {
      problems.push(label + ': has no title, so it was skipped.');
      continue;
    }
    var own = [];

    var etsyListingId = clean(get(row, 'LISTING_ID')) || null;
    if (etsyListingId && !/^\d+$/.test(etsyListingId)) {
      own.push('The Etsy listing number "' + etsyListingId + '" doesn’t look right, so it wasn’t kept.');
      etsyListingId = null;
    }
    if (etsyListingId && seenIds[etsyListingId]) {
      problems.push(label + ': is the same Etsy listing as row ' + seenIds[etsyListingId] + ', so it was skipped.');
      continue;
    }
    if (etsyListingId) seenIds[etsyListingId] = rowNumber;

    var priceText = get(row, 'PRICE');
    var price = parsePrice(priceText);
    if (price === null) {
      if (col.PRICE !== undefined) own.push(priceText.trim() ? 'The price "' + priceText.trim() + '" isn’t a number, so it was set to 0.' : 'It has no price, so it was set to 0.');
      price = 0;
    }

    var currency = clean(get(row, 'CURRENCY_CODE') || get(row, 'CURRENCY')).toUpperCase() || 'USD';
    if (currency !== 'USD') {
      own.push('Its price is in ' + currency + ', not US dollars. The shop sells in USD, so check the price after importing.');
    }

    var qtyText = get(row, 'QUANTITY');
    var quantity = parseQuantity(qtyText);
    if (quantity === null) {
      if (qtyText.trim()) own.push('The quantity "' + qtyText.trim() + '" isn’t a whole number, so stock was set to 0.');
      quantity = 0;
    }

    var imageUrls = [];
    for (var im = 1; im <= MAX_ETSY_IMAGES; im++) {
      var url = get(row, 'IMAGE' + im).trim();
      if (!url) continue;
      if (!isHttpsUrl(url)) { own.push('Photo ' + im + ' isn’t a web link, so it was left out.'); continue; }
      if (imageUrls.indexOf(url) < 0) imageUrls.push(url);
    }

    var variations = [];
    for (var vn = 1; vn <= MAX_ETSY_VARIATIONS; vn++) {
      var vType = clean(get(row, 'VARIATION_' + vn + '_TYPE'));
      var vName = clean(get(row, 'VARIATION_' + vn + '_NAME'));
      var vValues = splitList(get(row, 'VARIATION_' + vn + '_VALUES'));
      if (!vType && !vName && !vValues.length) continue;
      variations.push({ type: vType, name: vName, values: vValues });
    }

    var listing = {
      row: rowNumber,
      etsyListingId: etsyListingId,
      sku: clean(get(row, 'SKU')),
      name: name,
      description: decodeEntities(get(row, 'DESCRIPTION')).replace(/\r\n?/g, '\n').trim(),
      price: price,
      currency: currency,
      quantity: quantity,
      tags: splitList(get(row, 'TAGS')),
      materials: splitList(get(row, 'MATERIALS')),
      imageUrls: imageUrls,
      variations: variations,
      problems: own,
    };
    for (var op = 0; op < own.length; op++) problems.push(label + ': ' + own[op]);
    listings.push(listing);
  }

  if (!listings.length && rows.length > 1) problems.push('No listings could be read from the file.');
  if (rows.length === 1) problems.push('The file has headings but no listings.');

  return { listings: listings, problems: problems };
}

function shorten(text) {
  return text.length > 40 ? text.slice(0, 39) + '…' : text;
}
